/**
 * fetchExtData.js
 * ---------------------------------------------------------------
 * Builds payloads from the current feature selection and fetches
 * main + comparison external data in parallel.
 *
 * Returns { data, dataComparison } — caller is responsible for
 * calling setState with the result.
 *
 * Extracted from MainPage.fetchExtData.
 *
 * @param {object}  params.selectedFeature
 * @param {string}  params.selectedAreaLabel
 * @param {string}  params.selectedconstCode
 * @param {string}  params.selectedconstName
 * @returns {Promise<{ data: object|null, dataComparison: object|null }>}
 */
export async function fetchExtData({
  selectedFeature,
  selectedAreaLabel,
  selectedconstCode,
  selectedconstName,
}) {
  let payload = {};
  let payloadComparison = null;

  if (!selectedFeature) {
    payload.geoLevel = "constituency";
  } else {
    const featureLevel = selectedFeature.level;

    if (featureLevel === "ward") {
      const wardCode = selectedFeature?.value || selectedFeature?.rowMatchIds?.[0];
      if (!wardCode) {
        console.warn("Missing ward code, skipping external data fetch.");
        return { data: null, dataComparison: null };
      }
      payload.geoLevel = "ward";
      payload.wardNames = [wardCode];
      const constCode = selectedconstCode || selectedFeature?.properties?.PCON24CD;
      if (constCode) {
        payloadComparison = { geoLevel: "constituency", constituencyCodes: [constCode] };
      }
    } else if (featureLevel === "constituency") {
      const constCode = selectedFeature?.value || selectedconstCode;
      const constName = selectedFeature?.name || selectedconstName;
      if (!constCode && !constName) {
        console.warn("Missing constituency code/name, skipping external data fetch.");
        return { data: null, dataComparison: null };
      }
      payload.geoLevel = "ward";
      payload.constituencyCodes = [constCode];
      payloadComparison = { geoLevel: "constituency", constituencyCodes: [constCode] };
    } else {
      console.warn("Unsupported feature level, skipping external data fetch.");
      return { data: null, dataComparison: null };
    }
  }

  const fetchJson = async (p) => {
    const res = await fetch("/api/fetch_ext_data/", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(p),
    });
    if (!res.ok) {
      const text = await res.text();
      throw new Error(`fetch_ext_data failed (${res.status}): ${text}`);
    }
    return res.json();
  };

  const [data, dataComparison] = await Promise.all([
    fetchJson(payload),
    payloadComparison ? fetchJson(payloadComparison) : Promise.resolve(null),
  ]);

  return { data: data || null, dataComparison: dataComparison || null };
}
